import React, { PureComponent } from "react";
import swal from "sweetalert2";
import moment from "moment";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { connectStore } from "redux-box";
import { module as misc } from "store/misc";
import MiscApi from "@s/api/misc";
import {
  Heading,
  InputBox,
  ThemeButton,
  PlusButton,
  PlusIcon,
} from "@/static/styled/custom.styled";
import { ModalBackDrop, ModalView, CloseIconBar } from "@c/common/GlobalModal";
import {
  Wrapper,
  PopUpContainer,
  PopUpBody,
  FieldRow,
  FieldLabel,
  FieldControl,
  SelectHolder,
  LinkButton,
  CheckLabel,
  HelperText,
  TableHeader,
  FilterHolder,
  RecordsTable,
  OtpEditInput,
  Pager,
  EmptyText,
} from "./SchoolGradeMagicOtp.styled";

const OTP_LENGTH = 4;
const PAGE_SIZE = 20;
const SEARCH_DELAY = 400;
const ROLE_LABELS = { child: "Student", teacher: "Teacher" };

const ESC_KEY_CODE = 27;
const EMPTY_FORM = {
  selectedSchool: null,
  selectedGrade: null,
  roles: { child: true, teacher: false },
  otpInput: "",
};

const isValidOtp = (otp) => new RegExp(`^\\d{${OTP_LENGTH}}$`).test(otp);
const onlyDigits = (value) => (value || "").replace(/\D/g, "").slice(0, OTP_LENGTH);

class SchoolGradeMagicOtp extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      ...EMPTY_FORM,
      showForm: false,
      filterSchool: null,
      editingId: null,
      editingOtp: "",
    };
    this.searchTimer = null;
    this.pendingSetSchool = null;
  }

  componentDidMount() {
    this.props.misc.requestSchoolMagicOtpGrades(null);
    this.props.misc.requestSchoolMagicOtps(null, 0);
    document.addEventListener("keyup", this.handleKeyUp);
  }

  componentDidUpdate(prevProps) {
    // a set or an edit went through: close the row editor / the popup and show the full, unfiltered list
    if (prevProps.misc.schoolMagicOtpSavedAt !== this.props.misc.schoolMagicOtpSavedAt) {
      let nextState = { editingId: null, editingOtp: "" };
      if (this.pendingSetSchool) {
        nextState = {
          ...nextState,
          ...EMPTY_FORM,
          showForm: false,
          filterSchool: null,
        };
        this.pendingSetSchool = null;
        this.props.misc.requestSchoolMagicOtpGrades(null);
      }
      this.setState(nextState);
    }
  }

  componentWillUnmount() {
    clearTimeout(this.searchTimer);
    document.removeEventListener("keyup", this.handleKeyUp);
  }

  handleKeyUp = (e) => {
    if (e.keyCode === ESC_KEY_CODE && this.state.showForm) {
      this.handleFormClose();
    }
  };

  handleAddClickButton = () => {
    this.pendingSetSchool = null;
    this.props.misc.requestSchoolMagicOtpGrades(null);
    this.setState({ ...EMPTY_FORM, showForm: true, editingId: null, editingOtp: "" });
  };

  handleFormClose = () => {
    this.setState({ showForm: false });
  };

  handleBackDropClick = (e) => {
    if (e.target === e.currentTarget) {
      this.handleFormClose();
    }
  };

  showError = (text) => {
    swal({ title: "Error!", text, timer: 2000 });
  };

  loadSchoolOptions = (inputValue, callback) => {
    const text = (inputValue || "").trim();
    clearTimeout(this.searchTimer);
    if (text.length < 2) {
      callback([]);
      return;
    }
    this.searchTimer = setTimeout(() => {
      const query = encodeURIComponent(text);
      Promise.all([
        MiscApi.searchSchools(`name=${query}`),
        MiscApi.searchSchools(`code=${query}`),
      ])
        .then((responses) => {
          const seen = {};
          const options = [];
          responses.forEach((res) => {
            ((res && res.result) || []).forEach((school) => {
              if (!seen[school.id]) {
                seen[school.id] = true;
                options.push({
                  value: school.id,
                  label: `${school.name} (${school.school_code})`,
                });
              }
            });
          });
          callback(options);
        })
        .catch(() => callback([]));
    }, SEARCH_DELAY);
  };

  handleSchoolChange = (school) => {
    this.setState({ selectedSchool: school, selectedGrade: null });
    this.props.misc.requestSchoolMagicOtpGrades(school ? school.value : null);
  };

  handleGradeChange = (grade) => {
    this.setState({ selectedGrade: grade });
  };

  handleRoleChange = (role) => {
    this.setState({ roles: { ...this.state.roles, [role]: !this.state.roles[role] } });
  };

  handleOtpChange = (e) => {
    this.setState({ otpInput: onlyDigits(e.target.value) });
  };

  handleSave = () => {
    const { selectedSchool, selectedGrade, roles, otpInput } = this.state;
    const userRoles = Object.keys(roles).filter((role) => roles[role]);
    if (!selectedSchool) {
      this.showError("Select a school");
      return;
    }
    if (!selectedGrade) {
      this.showError("Select a grade");
      return;
    }
    if (!userRoles.length) {
      this.showError("Select at least one profile type");
      return;
    }
    if (!isValidOtp(otpInput)) {
      this.showError(`Magic OTP must be exactly ${OTP_LENGTH} digits`);
      return;
    }
    this.pendingSetSchool = selectedSchool;
    this.props.misc.saveSchoolMagicOtp({
      school: selectedSchool.value,
      user_roles: userRoles,
      grades: [selectedGrade.value],
      otp: otpInput,
    });
  };

  handleFilterChange = (school) => {
    this.setState({ filterSchool: school, editingId: null, editingOtp: "" });
    this.props.misc.requestSchoolMagicOtps(school ? school.value : null, 0);
  };

  handlePageChange = (offset) => {
    const { filterSchool } = this.state;
    this.setState({ editingId: null, editingOtp: "" });
    this.props.misc.requestSchoolMagicOtps(filterSchool ? filterSchool.value : null, offset);
  };

  handleEdit = (record) => {
    this.setState({ editingId: record.id, editingOtp: record.otp });
  };

  handleEditOtpChange = (e) => {
    this.setState({ editingOtp: onlyDigits(e.target.value) });
  };

  handleEditCancel = () => {
    this.setState({ editingId: null, editingOtp: "" });
  };

  handleEditSave = (record) => {
    const { editingOtp } = this.state;
    if (!isValidOtp(editingOtp)) {
      this.showError(`Magic OTP must be exactly ${OTP_LENGTH} digits`);
      return;
    }
    if (editingOtp === record.otp) {
      this.handleEditCancel();
      return;
    }
    this.props.misc.updateSchoolMagicOtp(record.id, editingOtp);
  };

  handleDelete = (record) => {
    const role = ROLE_LABELS[record.user_role] || record.user_role;
    swal({
      text: `Delete the magic OTP of ${record.school_name}, ${record.grade_name}, ${role}?`,
      type: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    }).then((result) => {
      if (result.value) {
        this.props.misc.removeSchoolMagicOtp(record.id);
      }
    });
  };

  renderForm() {
    const { schoolMagicOtpGrades, schoolMagicOtpSaving } = this.props.misc;
    const { selectedSchool, selectedGrade, roles, otpInput, showForm } = this.state;
    return (
      <div>
        <ModalBackDrop active={showForm} />
        <PopUpContainer active={showForm} onClick={this.handleBackDropClick}>
          <ModalView>
            <CloseIconBar onClick={this.handleFormClose}>
              <img src={require("static/img/white-cross.png")} alt="close icon" />
            </CloseIconBar>
            <PopUpBody>
              <Heading>Create School Magic OTP</Heading>
              <FieldRow>
                <FieldLabel>School</FieldLabel>
                <FieldControl>
                  <SelectHolder>
                    <AsyncSelect
                      cacheOptions
                      isClearable
                      loadOptions={this.loadSchoolOptions}
                      onChange={this.handleSchoolChange}
                      value={selectedSchool}
                      placeholder="Search by school name or code"
                      noOptionsMessage={() => "Type at least 2 characters"}
                    />
                  </SelectHolder>
                </FieldControl>
              </FieldRow>
              <FieldRow>
                <FieldLabel>Grade</FieldLabel>
                <FieldControl>
                  <SelectHolder>
                    <Select
                      isClearable
                      isDisabled={!selectedSchool}
                      options={schoolMagicOtpGrades}
                      onChange={this.handleGradeChange}
                      value={selectedGrade}
                      placeholder={selectedSchool ? "Select a grade" : "Select a school first"}
                    />
                  </SelectHolder>
                </FieldControl>
              </FieldRow>
              <FieldRow>
                <FieldLabel>Profile type</FieldLabel>
                <FieldControl>
                  {Object.keys(ROLE_LABELS).map((role) => (
                    <CheckLabel key={role}>
                      <input
                        type="checkbox"
                        checked={roles[role]}
                        onChange={() => this.handleRoleChange(role)}
                      />
                      {ROLE_LABELS[role]}
                    </CheckLabel>
                  ))}
                </FieldControl>
              </FieldRow>
              <FieldRow>
                <FieldLabel>Magic OTP</FieldLabel>
                <FieldControl>
                  <InputBox
                    type="text"
                    inputMode="numeric"
                    maxLength={OTP_LENGTH}
                    placeholder="4 digit OTP"
                    value={otpInput}
                    onChange={this.handleOtpChange}
                  />
                </FieldControl>
              </FieldRow>
              <ThemeButton primary onClick={this.handleSave} disabled={schoolMagicOtpSaving}>
                Set Magic OTP
              </ThemeButton>
              <HelperText>
                Works for every student / teacher of the selected school and grade until it is changed
                or deleted. Every grade needs its own OTP: one that another grade or school uses, or that
                matches the Global Magic OTP or a teacher's class OTP, is rejected.
              </HelperText>
            </PopUpBody>
          </ModalView>
        </PopUpContainer>
      </div>
    );
  }

  renderRow(record) {
    const { schoolMagicOtpSaving } = this.props.misc;
    const { editingId, editingOtp } = this.state;
    const isEditing = editingId === record.id;
    return (
      <tr key={record.id}>
        <td>
          {record.school_name} ({record.school_code})
        </td>
        <td>{record.grade_name}</td>
        <td>{ROLE_LABELS[record.user_role] || record.user_role}</td>
        <td>
          {isEditing ? (
            <OtpEditInput
              type="text"
              inputMode="numeric"
              maxLength={OTP_LENGTH}
              value={editingOtp}
              onChange={this.handleEditOtpChange}
              autoFocus
            />
          ) : (
            record.otp
          )}
        </td>
        <td>
          {moment(record.modified_at).format("DD MMM YYYY")}
          {record.created_by_name ? `, ${record.created_by_name}` : ""}
        </td>
        <td>
          {isEditing ? (
            <span>
              <LinkButton
                onClick={() => this.handleEditSave(record)}
                disabled={schoolMagicOtpSaving}
              >
                Save
              </LinkButton>
              <LinkButton onClick={this.handleEditCancel}>Cancel</LinkButton>
            </span>
          ) : (
            <span>
              <LinkButton onClick={() => this.handleEdit(record)}>Edit</LinkButton>
              <LinkButton
                danger
                onClick={() => this.handleDelete(record)}
                disabled={schoolMagicOtpSaving}
              >
                Delete
              </LinkButton>
            </span>
          )}
        </td>
      </tr>
    );
  }

  renderRecords() {
    const { schoolMagicOtpList, schoolMagicOtpPage, schoolMagicOtpLoading } = this.props.misc;
    const { filterSchool } = this.state;
    const { offset, count, hasNext } = schoolMagicOtpPage;
    const first = offset * PAGE_SIZE + 1;
    const last = offset * PAGE_SIZE + schoolMagicOtpList.length;
    return (
      <div>
        <TableHeader>
          <Heading>School Magic OTPs</Heading>
          <FilterHolder>
            <AsyncSelect
              cacheOptions
              isClearable
              loadOptions={this.loadSchoolOptions}
              onChange={this.handleFilterChange}
              value={filterSchool}
              placeholder="Filter by school (all schools)"
              noOptionsMessage={() => "Type at least 2 characters"}
            />
          </FilterHolder>
        </TableHeader>
        <RecordsTable>
          <thead>
            <tr>
              <th>School</th>
              <th>Grade</th>
              <th>Profile type</th>
              <th>OTP</th>
              <th>Updated</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>{schoolMagicOtpList.map((record) => this.renderRow(record))}</tbody>
        </RecordsTable>
        {!schoolMagicOtpList.length ? (
          <EmptyText>
            {schoolMagicOtpLoading ? "Loading..." : "No school magic OTPs found"}
          </EmptyText>
        ) : (
          <Pager>
            <LinkButton
              onClick={() => this.handlePageChange(offset - 1)}
              disabled={offset === 0 || schoolMagicOtpLoading}
            >
              Prev
            </LinkButton>
            <span>
              {first}-{last} of {count}
            </span>
            <LinkButton
              onClick={() => this.handlePageChange(offset + 1)}
              disabled={!hasNext || schoolMagicOtpLoading}
            >
              Next
            </LinkButton>
          </Pager>
        )}
      </div>
    );
  }

  render() {
    return (
      <Wrapper>
        {this.renderRecords()}
        {this.renderForm()}
        <PlusButton hide={this.state.showForm} onClick={this.handleAddClickButton}>
          <PlusIcon primary alt="Plus Icon" src={require("static/img/plusicon.png")} />
        </PlusButton>
      </Wrapper>
    );
  }
}

export default connectStore({
  misc,
})(SchoolGradeMagicOtp);
